import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Play, Truck, ShieldCheck, Sparkles } from "lucide-react";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_design-your-site-12/artifacts/nqz9km26_standup.png";

export default function Hero() {
  return (
    <section
      data-testid="hero-section"
      id="top"
      className="relative overflow-hidden bg-white"
    >
      {/* Industrial grid backdrop */}
      <div className="absolute inset-0 pk-grid-bg opacity-60 pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#F58220]/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#1B3F8F]/10 blur-3xl pointer-events-none" />

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 pt-12 md:pt-20 pb-16 md:pb-24">
        {/* Eyebrow row */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] font-bold text-[#1B3F8F] mb-8"
        >
          <span className="inline-block w-8 h-px bg-[#F58220]" />
          A Pacific Group Brand
          <span className="hidden sm:inline-block w-8 h-px bg-[#F58220]" />
        </motion.div>

        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
          {/* Headline */}
          <div className="col-span-12 lg:col-span-8">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.05 }}
              className="font-display font-black uppercase tracking-[-0.02em] text-[44px] leading-[0.95] sm:text-6xl md:text-7xl lg:text-[92px]"
            >
              Smart packaging
              <br />
              for every <span className="text-[#F58220]">brand</span>
              <br />
              <span className="text-[#1B3F8F]">ambition.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-[#0a1128]/80 font-medium"
            >
              Customised flexible pouches with{" "}
              <span className="font-bold text-[#0a1128]">low MOQs</span>,
              premium printing and a{" "}
              <span className="font-bold text-[#F58220]">7-day delivery</span>{" "}
              promise. No cylinders. No surprises. Just packaging that earns
              shelf-space.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.35 }}
              className="mt-10 flex flex-wrap items-center gap-3"
            >
              <a
                href="#quote"
                data-testid="hero-cta-quote"
                className="group inline-flex items-center gap-3 bg-[#F58220] hover:bg-[#d9701a] text-white px-7 py-4 text-[13px] font-bold uppercase tracking-[0.16em] transition-colors"
              >
                Get an instant quote
                <ArrowRight
                  size={18}
                  className="transition-transform group-hover:translate-x-1"
                />
              </a>
              <a
                href="#products"
                data-testid="hero-cta-explore"
                className="inline-flex items-center gap-2 border-2 border-[#1B3F8F] text-[#1B3F8F] hover:bg-[#1B3F8F] hover:text-white px-7 py-4 text-[13px] font-bold uppercase tracking-[0.16em] transition-colors"
              >
                <Play size={14} className="fill-current" />
                Explore products
              </a>
            </motion.div>

            {/* Trust strip */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.55 }}
              className="mt-12 grid grid-cols-3 gap-6 max-w-xl"
            >
              <Trust icon={<Truck size={16} />} label="7-day delivery" />
              <Trust icon={<ShieldCheck size={16} />} label="FDA-grade films" />
              <Trust
                icon={<Sparkles size={16} />}
                label="Printed pouches"
              />
            </motion.div>
          </div>

          {/* Right image card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="col-span-12 lg:col-span-4 relative"
          >
            <div className="relative aspect-[3/4] w-full overflow-hidden border border-[#d1d5db] bg-[#F8F9FA]">
              <img
                src={HERO_IMG}
                alt="Stand-up pouches"
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a1128]/40 via-transparent to-transparent" />

              {/* Bottom info card */}
              <div className="absolute bottom-4 left-4 right-4 bg-white border border-[#d1d5db] p-4">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#1B3F8F]">
                  Today’s lead time
                </div>
                <div className="mt-1 flex items-end justify-between">
                  <div className="font-display text-4xl font-black text-[#0a1128]">
                    7 days
                  </div>
                  <div className="text-[11px] font-semibold text-[#0a1128]/60 max-w-[120px] text-right">
                    From artwork to door, anywhere in India.
                  </div>
                </div>
              </div>
            </div>

            {/* Floating stat */}
            <div className="hidden md:block absolute -bottom-6 -left-6 bg-[#1B3F8F] text-white px-5 py-4 shadow-xl">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-70">
                Avg. MOQ
              </div>
              <div className="font-display text-2xl font-black">
                5000 <span className="text-[#F58220]">pcs</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Trust({ icon, label }) {
  return (
    <div className="flex items-center gap-2 text-[#0a1128]">
      <span className="inline-flex items-center justify-center w-8 h-8 bg-[#1B3F8F] text-white">
        {icon}
      </span>
      <span className="text-[12px] font-bold uppercase tracking-[0.12em]">
        {label}
      </span>
    </div>
  );
}
